import React, { useEffect, useRef, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import { logger } from 'packages/logger';
import * as loginAnalytics from 'tradera/legacy/static/app/ui/layout/login-signup-analytics';
import Analytics from 'packages/analytics';
import { useUrlLocalizer, useCurrentLanguage } from 'tradera/lang/translate';
import { loadScript } from 'tradera/utils/script';
import { getLoginLocaleFromLanguage } from './get-login-locale-from-language';

const GoogleLoginButton = ({
    googleAppId,
    isModal,
    onError,
    className,
    disabled,
    compact
}) => {
    const { toLocalizedUrl } = useUrlLocalizer();

    const [googleButtonInitialized, setGoogleButtonInitialized] =
        useState(false);
    const buttonRef = useRef(null);
    const iconSize = useRef(compact);

    const { language } = useCurrentLanguage();
    const locale = getLoginLocaleFromLanguage(language);

    const performLogin = useCallback(
        async (id_token) => {
            try {
                const verifyResponse = await axios.post(
                    '/login/google/verify' + window.location.search,
                    {
                        accessToken: id_token,
                        UserId: 'NOT_USED'
                    }
                );

                if (isModal) {
                    Analytics.trackEvent({
                        category: 'Top navigation',
                        action: 'Login menu',
                        label: 'Button - Google login'
                    });
                }
                loginAnalytics.flow.set(loginAnalytics.FLOW.LOGIN);
                loginAnalytics.method.set(loginAnalytics.METHOD.GOOGLE);
                loginAnalytics.trackEvent('user-login-submit');

                window.top.location = verifyResponse.data.returnUrl;
            } catch (error) {
                const message =
                    error?.response?.data?.error ||
                    error?.message ||
                    error?.error;
                switch (message) {
                    case 'popup_closed_by_user':
                        break;
                    case 'MemberAuth_IncorrectUserOrPassword':
                        window.top.location = toLocalizedUrl(
                            '/login?connect=Google'
                        );
                        break;
                    default:
                        onError(error);
                        logger(message);
                        break;
                }
            }
        },
        [isModal, onError, toLocalizedUrl]
    );

    useEffect(() => {
        const setIcon = () => {
            window.google.accounts.id.renderButton(buttonRef.current, {
                locale: locale,
                theme: 'outline',
                size: compact ? 'large' : 'medium',
                type: compact ? 'icon' : 'standard',
                text: 'signin_with',
                shape: compact ? 'circle' : 'rectangular',
                width: buttonRef.current.scrollWidth
            });
        };

        if (googleButtonInitialized) {
            if (iconSize !== compact) {
                setIcon();
            }

            return;
        }

        const initializeGsi = () => {
            if (!window.google) return;

            if (!googleButtonInitialized) {
                window.google.accounts.id.initialize({
                    client_id: googleAppId,
                    callback: async (callback) => {
                        if (disabled) {
                            return;
                        }

                        onError(null);
                        try {
                            await performLogin(callback.credential);
                        } catch (error) {
                            onError(error);
                        }
                    }
                });
                setGoogleButtonInitialized(true);
            }

            setIcon();
        };

        loadScript('https://accounts.google.com/gsi/client')
            .then(initializeGsi)
            .catch(() => {});
    }, [
        buttonRef,
        googleAppId,
        disabled,
        onError,
        performLogin,
        compact,
        locale,
        googleButtonInitialized,
        iconSize
    ]);

    return <div ref={buttonRef} className={className} />;
};

GoogleLoginButton.propTypes = {
    className: PropTypes.string,
    googleAppId: PropTypes.string,
    isModal: PropTypes.bool,
    onError: PropTypes.func,
    disabled: PropTypes.bool,
    compact: PropTypes.bool
};

GoogleLoginButton.defaultProps = {
    className: '',
    tabIndex: 0
};
export default GoogleLoginButton;
